/**
 * BrowseProductsPage (iter86) — Catalog landing.
 *
 * Layout (top→bottom):
 *   1. Search header
 *   2. Group Categories chip row (filters, taps to /g/:slug)
 *   3. Admin-curated Home Sections (carousels of products)
 *   4. "All products" infinite-scroll feed
 *
 * iter83 originally rendered a static Group Categories GRID as the
 * body. iter86 swaps the grid for the richer Flipkart-style layout
 * (chips on top, then home sections, then a paginated product feed).
 *
 * Routes:
 *   • ``/products``                 — every vertical
 *   • ``/c/:slug/products``         — scoped to one vertical
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Package, Search, ChevronRight, CalendarClock } from "lucide-react";
import CustomerHeader from "../../components/CustomerHeader";
import VoiceSearch from "../../components/VoiceSearch";
import GroupCategoryChipRow from "../../components/GroupCategoryChipRow";
import StockBadge from "../../components/StockBadge";
import InlineCartControl from "../../components/InlineCartControl";
import BookingModal from "../../components/BookingModal";
import { api, inr } from "../../lib/api";
import { useTenant } from "../../contexts/TenantContext";
import { useAuth } from "../../contexts/AuthContext";
import { t } from "../../lib/i18n";
import { useIsBlinkit } from "../../lib/storefrontTheme";
import BkCatalogView from "./blinkit/BkCatalogView";

// Blinkit-theme tenants get the left-rail category browser; every other theme keeps this page unchanged.
export default function BrowseProductsPage() {
    const { slug } = useParams();
    return useIsBlinkit() ? <BkCatalogView key={slug || "all"} verticalSlug={slug || null} /> : <ClassicBrowseProductsPage />;
}

function ClassicBrowseProductsPage() {
    const { slug } = useParams();                     // optional vertical
    const { lang } = useTenant();
    const { user } = useAuth();
    const [vertical, setVertical] = useState(null);
    const [verticalLookupReady, setVerticalLookupReady] = useState(false);
    const [sections, setSections] = useState(null);   // null = loading
    const [allItems, setAllItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [cartMap, setCartMap] = useState({});       // {product_id: qty}
    const sentinelRef = useRef(null);

    // Load / refresh the cart-qty cache so every inline control on the
    // page renders the current quantity. Refetches whenever any card
    // dispatches ``cart:update``.
    useEffect(() => {
        let alive = true;
        const load = async () => {
            if (!user) { setCartMap({}); return; }
            try {
                const { data } = await api.get("/cart");
                if (!alive) return;
                const m = {};
                for (const it of (data?.items || [])) {
                    // Aggregate qty across variants of the same product so
                    // the listing card's inline control reflects total qty.
                    m[it.product_id] = (m[it.product_id] || 0) + (it.quantity || 0);
                }
                setCartMap(m);
            } catch {
                if (alive) setCartMap({});
            }
        };
        load();
        window.addEventListener("cart:update", load);
        return () => {
            alive = false;
            window.removeEventListener("cart:update", load);
        };
    }, [user]);

    // Step 1 — resolve the optional vertical so we can scope every
    // downstream fetch (groups + home sections + all products).
    useEffect(() => {
        if (!slug) {
            setVertical(null);
            setVerticalLookupReady(true);
            return;
        }
        api.get("/business-categories")
            .then(({ data }) => {
                const v = (data || []).find((x) => x.slug === slug) || null;
                setVertical(v);
                setVerticalLookupReady(true);
            })
            .catch(() => setVerticalLookupReady(true));
    }, [slug]);

    // Step 2 — load admin-curated home sections (uses the existing
    // marketplace endpoint that already hydrates products + shop info).
    useEffect(() => {
        if (!verticalLookupReady) return;
        setSections(null);
        api.get("/marketplace/home-sections")
            .then(({ data }) => {
                let secs = data || [];
                // Vertical-scoped page → keep only sections that contain
                // at least one product from a shop in that vertical.
                // We don't have section.business_category_id, so this is
                // best-effort post-filter.
                if (vertical?.id) {
                    secs = secs.map((s) => ({
                        ...s,
                        products: (s.products || []).filter((p) => p && p.business_category_ids
                            ? p.business_category_ids.includes(vertical.id)
                            : true),
                    })).filter((s) => s.products.length > 0);
                }
                setSections(secs);
            })
            .catch(() => setSections([]));
    }, [verticalLookupReady, vertical?.id]);

    // Step 3 — paginated "All products" feed. We reload on vertical
    // change and lazy-fetch more pages as the sentinel comes into view.
    useEffect(() => {
        if (!verticalLookupReady) return;
        // Reset on vertical change so we don't mix verticals' products.
        setAllItems([]);
        setNextCursor(null);
        setHasMore(true);
        loadMore(null);
    }, [verticalLookupReady, vertical?.id]);

    const loadMore = async (cursor) => {
        if (loadingMore) return;
        setLoadingMore(true);
        try {
            const params = { limit: 24 };
            if (cursor) params.cursor = cursor;
            if (vertical?.id) params.business_category_id = vertical.id;
            const { data } = await api.get("/products", { params });
            setAllItems((prev) => cursor ? [...prev, ...(data.items || [])] : (data.items || []));
            setNextCursor(data.next_cursor);
            setHasMore(!!data.next_cursor);
        } catch {
            setHasMore(false);
        } finally {
            setLoadingMore(false);
        }
    };

    // IntersectionObserver-backed infinite scroll. The sentinel sits
    // below the feed and triggers ``loadMore`` once it enters viewport.
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !hasMore || !nextCursor) return;
        const obs = new IntersectionObserver((entries) => {
            for (const e of entries) {
                if (e.isIntersecting) { loadMore(nextCursor); break; }
            }
        }, { rootMargin: "200px" });
        obs.observe(el);
        return () => obs.disconnect();
    }, [nextCursor, hasMore]);

    const heading = useMemo(() => {
        if (slug && vertical) return `${vertical.name} · Catalog`;
        if (slug) return slug.charAt(0).toUpperCase() + slug.slice(1);
        return "Catalog";
    }, [slug, vertical]);

    return (
        <div className="pb-32" data-testid="browse-products-page">
            <CustomerHeader>
                <Link to="/search" className="flex items-center justify-between bg-muted rounded-xl px-4 py-3 group active:scale-[0.98] transition">
                    <div className="flex items-center gap-3 text-muted-foreground">
                        <Search size={18} />
                        <span className="text-sm">{t("search_placeholder", lang)}</span>
                    </div>
                    <VoiceSearch />
                </Link>
            </CustomerHeader>

            <header className="px-4 mt-3" data-testid="browse-products-header">
                <h1 className="text-lg font-bold">{heading}</h1>
                <p className="text-xs text-muted-foreground">
                    Tap a category to filter — or browse all products below.
                </p>
            </header>

            {/* 1. Group Category chip row (filters → /g/:slug). */}
            <GroupCategoryChipRow
                businessCategoryId={vertical?.id}
                testIdPrefix="prod-group-chips"
            />

            {/* 2. Admin-curated Home Sections (carousels). */}
            <HomeSectionsList sections={sections} cartMap={cartMap} />

            {/* 3. All-products feed. */}
            <section className="px-4 mt-6" data-testid="all-products-section">
                <h2 className="font-bold text-base mb-3">All products</h2>
                {allItems.length === 0 && !loadingMore ? (
                    <div className="py-8 text-center text-sm text-muted-foreground" data-testid="all-products-empty">
                        <Package size={28} className="mx-auto mb-2 opacity-50" />
                        <p>No products available yet.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3" data-testid="all-products-grid">
                        {allItems.map((p) => <MarketplaceProductCard key={p.id} product={p} cartQty={cartMap[p.id] || 0} />)}
                    </div>
                )}
                {/* Sentinel for the infinite-scroll IntersectionObserver
                    above — stays in the DOM at all times (needed for the
                    observer to attach) but only reserves visible height
                    while a page is actively loading. No "end of catalog"
                    marker — user-requested removal; the feed just stops
                    growing silently once the backend returns no cursor. */}
                <div
                    ref={sentinelRef}
                    className={
                        loadingMore
                            ? "flex items-center justify-center pt-4 pb-2 text-muted-foreground text-xs"
                            : "h-0"
                    }
                    data-testid="catalog-sentinel"
                >
                    {loadingMore && <><Loader2 size={14} className="animate-spin mr-2" /> Loading more…</>}
                </div>
            </section>
        </div>
    );
}


/**
 * Home Sections list — admin-authored carousels of products. Each
 * section renders as a header (title + subtitle) + horizontal-scroll
 * row of product cards. Hidden entirely when ``sections`` is empty.
 */
function HomeSectionsList({ sections, cartMap = {} }) {
    if (sections === null) {
        return (
            <div className="px-4 mt-6 space-y-4" data-testid="home-sections-loading">
                {[0, 1].map((i) => (
                    <div key={i} className="space-y-2">
                        <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                        <div className="flex gap-3 overflow-hidden">
                            {[0, 1, 2].map((j) => (
                                <div key={j} className="w-[140px] h-[200px] bg-muted/40 rounded-xl animate-pulse flex-shrink-0" />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    if (sections.length === 0) return null;

    return (
        <div className="mt-2" data-testid="home-sections">
            {sections.map((s) => (
                <section key={s.id} className="mt-4" data-testid={`home-section-${s.slug}`}>
                    <header className="px-4 flex items-end justify-between mb-2">
                        <div>
                            <h2 className="font-bold text-base">{s.title}</h2>
                            {s.subtitle && <p className="text-xs text-muted-foreground">{s.subtitle}</p>}
                        </div>
                        <span className="text-[11px] text-muted-foreground">{s.products.length} {s.products.length === 1 ? "item" : "items"}</span>
                    </header>
                    <div className="flex gap-3 overflow-x-auto px-4 pb-2 no-scrollbar">
                        {s.products.map((p) => <MarketplaceProductCard key={p.id} product={p} cartQty={cartMap[p.id] || 0} compact />)}
                    </div>
                </section>
            ))}
        </div>
    );
}


/**
 * Compact product card used by both the home-section carousels and the
 * "All products" grid. Tapping the image / text navigates into the PDP
 * (``/product/{pid}``) — the inline cart control (iter115) sits in the
 * corner so customers can ADD / adjust qty without opening the PDP,
 * Amazon/Flipkart-style. The PDP flow itself is unchanged.
 */
function MarketplaceProductCard({ product, cartQty = 0, compact }) {
    const img = (product.images && product.images[0]) || product.image_url;
    const widthCls = compact ? "w-[150px] flex-shrink-0" : "";
    // iter116 — show a small hint when the product has selectable
    // variants so shoppers know tapping ADD will open the picker.
    const variantCount = (product.variants || []).filter((v) => v?.enabled !== false).length;
    // Service/booking products (``kind === "SERVICE"``) can't be added to
    // a cart — ``InlineCartControl`` deliberately renders nothing for
    // them (see its own docstring), which left this card with NO action
    // at all in the Catalog tab even though the exact same product shows
    // a working "BOOK NOW" button via ProductCard.jsx in the Shops tab
    // (user-reported inconsistency). Mirror that same BookingModal flow
    // here so both entry points behave identically.
    const isService = product?.kind === "SERVICE";
    const [bookingOpen, setBookingOpen] = useState(false);
    return (
        <div
            data-testid={`mp-product-${product.id}`}
            className={`bg-card border border-border rounded-2xl overflow-hidden flex flex-col ${widthCls}`}
        >
            <Link
                to={`/product/${product.id}`}
                data-testid={`mp-product-link-${product.id}`}
                className="block active:scale-[0.98] transition"
            >
                <div className="aspect-square bg-muted/30 overflow-hidden">
                    {img ? (
                        <img src={img} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            <Package size={26} />
                        </div>
                    )}
                </div>
                <div className="px-2.5 pt-2.5">
                    <p className="font-bold text-sm leading-tight line-clamp-2 min-h-[2.5em]">{product.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">{product.shop_name || product.restaurant_name}</p>
                    <div className="mt-1"><StockBadge product={product} /></div>
                </div>
            </Link>
            {/* Price row */}
            <Link
                to={`/product/${product.id}`}
                className="px-2.5 mt-2 flex items-baseline gap-2 flex-wrap"
                aria-label={`View ${product.name} details`}
            >
                <span className="font-bold text-sm">{inr(product.selling_price)}</span>
                {product.mrp && product.mrp > product.selling_price && (
                    <span className="text-[11px] text-muted-foreground line-through">{inr(product.mrp)}</span>
                )}
            </Link>
            {/* Variant availability hint — appears only when the merchant
                configured 2+ options, so users know tapping ADD opens
                the picker. Kept short so it never wraps. */}
            {variantCount >= 2 && (
                <p className="px-2.5 mt-1 text-[10px] uppercase tracking-wider text-primary font-bold" data-testid={`mp-variant-hint-${product.id}`}>
                    {variantCount} options
                </p>
            )}
            {/* Action row — services get a "BOOK NOW" button that opens
                the shared BookingModal (same flow as ProductCard.jsx's
                Shops-tab card); everything else gets the normal
                add-to-cart control. Full-width so it can never overflow
                the card and clip the "+" button. */}
            <div className="px-2.5 pb-2.5 pt-2 flex justify-stretch">
                {isService ? (
                    <button
                        data-testid={`mp-book-now-${product.id}`}
                        onClick={() => setBookingOpen(true)}
                        className="w-full bg-card text-primary border-2 border-primary font-bold tracking-wider text-xs px-3 py-2 rounded-xl active:scale-95 transition inline-flex items-center justify-center gap-1"
                    >
                        <CalendarClock size={12} /> BOOK NOW
                    </button>
                ) : (
                    <InlineCartControl product={product} currentQty={cartQty} size="sm" fullWidth />
                )}
            </div>
            {bookingOpen && <BookingModal service={product} onClose={() => setBookingOpen(false)} />}
        </div>
    );
}
